import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getWorkOrderById, getBudgetHeadDropdown, getPaymentsByWorkOrder, addWorkPayment } from '../../services/AppService';
import closeButton from "../../assets/images/close.png";
import PageFade from '../../components/Login/PageFade';
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from '../../components/AdminComponents/Modals/ConfirmActionModal';
import { resolveMediaUrl } from '../../utility/media';

const WorkPaymentForm = () => {
    const { workOrderId } = useParams();
    const { state } = useLocation();
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Data States
    const [workOrder, setWorkOrder] = useState(null);
    const [budgetHeads, setBudgetHeads] = useState([]);
    const [paymentHistory, setPaymentHistory] = useState([]);

    const [isHovering, setIsHovering] = useState(false);

    // Form States
    const [formData, setFormData] = useState({ budgetHeadId: "", paymentAmount: "" });
    const [selectedFile, setSelectedFile] = useState(null);
    const [financials, setFinancials] = useState({ paid: 0, balance: 0, selectedHeadAmount: 0 });

    // UI States
    const [confirmModal, setConfirmModal] = useState({ open: false });
    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });

    const loadData = useCallback(async () => {
        try {
            const [wo, bh, history] = await Promise.all([
                getWorkOrderById(workOrderId),
                getBudgetHeadDropdown(),
                getPaymentsByWorkOrder(workOrderId)
            ]);

            setWorkOrder(wo);
            setBudgetHeads(bh.content || []);
            setPaymentHistory(history);

            // Calculate paid amount from history
            const totalPaid = history.reduce((sum, p) => sum + p.paymentAmount, 0);
            setFinancials(prev => ({
                ...prev,
                paid: totalPaid,
                balance: wo.bidAmount - totalPaid
            }));
        } catch (error) { console.error(error); }
    }, [workOrderId]);

    useEffect(() => { loadData(); }, [loadData]);

    const handleBudgetHeadChange = (id) => {
        const selected = budgetHeads.find(h => h.id === Number(id));
        setFormData({ ...formData, budgetHeadId: id });
        setFinancials(prev => ({ ...prev, selectedHeadAmount: selected?.budgetAmount || 0 }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // if (Number(formData.paymentAmount) > financials.balance) {
        //     setMessageModal({ open: true, type: "error", title: "Limit Exceeded", message: "Payment cannot exceed balance amount." });
        //     return;
        // }
        setConfirmModal({ open: true });
    };

    const handleConfirmSave = async () => {
        setConfirmModal({ open: false });
        const data = new FormData();
        const payload = {
            workOrderId: Number(workOrderId),
            budgetHeadId: Number(formData.budgetHeadId),
            paymentAmount: Number(formData.paymentAmount)
        };

        data.append("payment", new Blob([JSON.stringify(payload)], { type: "application/json" }));
        if (selectedFile) data.append("file", selectedFile);

        try {
            await addWorkPayment(data);
            setMessageModal({ open: true, type: "success", title: "Success", message: "Payment recorded successfully" });
            setFormData({ budgetHeadId: "", paymentAmount: "" });
            setSelectedFile(null);
            loadData();
        } catch (error) {
            setMessageModal({ open: true, type: "error", title: "Error", message: "Transaction Failed" });
        }
    };

    return (
        <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-6 relative">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h4 className="text-xl font-semibold text-blue uppercase tracking-wide">{t('pageTitles.workPaymentList')}</h4>
                    <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
                </div>

                <div className="mb-6 text-sm relative">
                    <strong>{t("accounts.payment.workDetails")} : </strong>
                    <span
                        className="text-blue-600 underline font-bold uppercase cursor-pointer"
                        onMouseEnter={() => setIsHovering(true)}
                        onMouseLeave={() => setIsHovering(false)}
                    >
                        {workOrder?.workTitle || state?.workTitle || "Loading..."}
                    </span>

                    {isHovering && workOrder && (
                        <div className="absolute z-50 top-6 left-0 bg-[#6b7ecb] text-white p-4 rounded-lg shadow-xl border w-full max-w-2xl transition-opacity duration-300">
                            <div className="grid grid-cols-2 gap-y-2 font-semibold">
                                <div>{t("work.workOrderNo")} : <span className="font-bold">{workOrder.workOrderNo}</span></div>
                                <div>{t("work.orderDate")}: <span className="font-bold">{workOrder.workOrderDate}</span></div>
                                <div>{t("approvals.workStartDate")}: <span className="font-bold">{workOrder.workStartDate}</span></div>
                                <div>{t("approvals.workEndDate")}: <span className="font-bold">{workOrder.workEndDate}</span></div>
                                <div>{t("approvals.workDuration")}: <span className="font-bold">{workOrder.duration} Days</span></div>
                                <div>Status: <span className="font-bold">{workOrder.isCompleted ? "Completed" : "In Progress"}</span></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Form Section */}
                <form className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-6 mb-8 bg-gray-50 p-8 rounded-xl border border-gray-200" onSubmit={handleSubmit}>
                    <InputRow label={t("accounts.payment.budgetHead")} type="select" options={budgetHeads} value={formData.budgetHeadId} onChange={handleBudgetHeadChange} />
                    <InputRow label={t("accounts.payment.budgetAmount")} value={financials.selectedHeadAmount.toLocaleString('en-IN')} disabled placeholder="Budget Amount" />
                    <InputRow label={t("accounts.payment.balanceAmount")} value={financials.balance.toLocaleString('en-IN')} disabled placeholder="Pending Amount" />

                    <InputRow label={t("accounts.payment.bidAmount")} value={workOrder?.bidAmount?.toLocaleString('en-IN')} disabled />
                    <InputRow label={t("accounts.payment.paidAmount")} value={financials.paid.toLocaleString('en-IN')} disabled />
                    <InputRow label={t("accounts.payment.finalBalanceAmount")} value={(financials.balance - (formData.paymentAmount || 0)).toLocaleString('en-IN')} disabled />

                    <InputRow label={t("accounts.payment.paymentAmount")} type="number" value={formData.paymentAmount} onChange={v => setFormData({ ...formData, paymentAmount: v })} placeholder="Payment Amount" />

                    <div className="flex items-center text-sm">
                        <label className="w-32 font-bold text-gray-600 text-right">{t("common.attachments")}</label>
                        <span className="px-3 font-bold text-gray-800">:</span>
                        <input type="file" className="flex-1 border rounded p-1 bg-white text-xs" onChange={e => setSelectedFile(e.target.files[0])} />
                    </div>

                    <div className="col-span-3 text-center mt-4">
                        <button type="submit" className="staff-btn-save">{t("accounts.payment.addPayment")}</button>
                    </div>
                </form>

                {/* History Table */}
                <div className="overflow-x-auto rounded-lg border">
                    <table className="w-full text-sm ctable">
                        <thead className="bg-[#a0adf1] text-white">
                            <tr>
                                <th className="p-2 border-b border-black w-16 text-center">{t('tables.srNo')}</th>
                                <th className="p-2 border-b border-black text-left">{t('accounts.payment.budgetHead')}</th>
                                <th className="p-2 border-b border-black text-right">{t('accounts.payment.paymentAmount')}</th>
                                <th className="p-2 border-b border-black text-center">{t('common.attachments')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paymentHistory.length === 0 ? (
                                <tr><td colSpan="4" className="text-center py-4 italic text-gray-500">No payment history found</td></tr>
                            ) : (
                                paymentHistory.map((p, i) => (
                                    <tr key={p.id} className="hover:bg-gray-50 border-b">
                                        <td className="p-2 border-r text-center">{i + 1}</td>
                                        <td className="p-2 border-r">{p.budgetName}</td>
                                        <td className="p-2 border-r text-right font-bold">{p.paymentAmount?.toLocaleString('en-IN')}</td>
                                        <td className="p-2 text-center">
                                            {p.attachmentLocation && (
                                                <a href={resolveMediaUrl(p.attachmentLocation)} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs font-bold truncate inline-block max-w-[200px]">
                                                    {p.attachmentName}
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmActionModal open={confirmModal.open} onClose={() => setConfirmModal({ open: false })} onConfirm={handleConfirmSave} title="Confirm Payment" message="Are you sure you want to record this payment?" />
            <MessageModal open={messageModal.open} onClose={() => setMessageModal({ ...messageModal, open: false })} {...messageModal} />
        </PageFade>
    );
};

const InputRow = ({ label, value, onChange, type = "text", disabled = false, options = [], placeholder = "" }) => (
    <div className="flex items-center text-sm">
        <label className="w-32 font-bold text-gray-600 text-right">{label}</label>
        <span className="px-3 font-bold text-gray-800">:</span>
        {type === "select" ? (
            <select className="flex-1 border-2 p-1.5 rounded-lg bg-white" value={value} onChange={e => onChange(e.target.value)} required>
                <option value="">Select Budget Head</option>
                {options.map(opt => <option key={opt.id} value={opt.id}>{opt.budgetName}</option>)}
            </select>
        ) : (
            <input type={type} className={`flex-1 border-2 p-1.5 rounded-lg ${disabled ? 'bg-gray-200 font-bold' : 'bg-white'}`} value={value} onChange={e => onChange(e.target.value)} disabled={disabled} placeholder={placeholder} required />
        )}
    </div>
);

export default WorkPaymentForm;